<script lang="ts">
  import { getModalStore, getToastStore, ProgressRadial } from '@skeletonlabs/skeleton'
  import { onMount, type SvelteComponent } from 'svelte'

  import {
    displayLlmSourceTypes,
    HuggingFaceTransformersSource,
    type ICreateLlm,
    type ILlm,
    isHuggingFaceTransformersSource,
    isLocalTransformersSource,
    LlmSourceType,
    llmSourceTypesValues,
    LocalTransformersSource,
  } from './llms-store.svelte'
  import { getRestrictedModelConfig, isModelSourceApprovedForUnlearning } from './restricted-models'
  import { openRunOptionsModal } from '../runs/llm-runs'
  import CloseModalButton from '@components/CloseModalButton.svelte'
  import InvalidHoverInfo from '@components/InvalidHoverInfo.svelte'
  import InvalidIcon from '@components/InvalidIcon.svelte'
  import MetadataFields from '@components/MetadataFields.svelte'
  import ReadOnly from '@components/ReadOnly.svelte'
  import RequiredNameField from '@components/RequiredNameField.svelte'
  import Select from '@components/Select.svelte'
  import {
    createSaveHuggingFaceTokenMutation,
    persistSavedHuggingFaceToken,
  } from '@routes/huggingface-tokens/huggingface-tokens-store'
  import HuggingFaceTokenPicker from '@routes/huggingface-tokens/HuggingFaceTokenPicker.svelte'
  import {
    organizationAsyncState,
    organizationsStore,
  } from '@routes/organizations/organizations-store'
  import { loggedInStore, User } from '@stores/logged-in-store'
  import { type Config, getConfig } from '@utils/config.svelte'
  import { checkHuggingFaceModelAccess } from '@utils/hf'
  import { setRestoreState } from '@utils/modal-restore'
  import {
    formatParameterCountShort,
    getLlmTierValidationMessage,
    type LlmTieredProduct,
    Product,
  } from '@utils/product'
  import { queryClient } from '@utils/svelte-query-client'
  import {
    HUGGINGFACE_REPO_NAME_PATTERN,
    huggingfaceRepoNameValidator,
    ValidityState,
  } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()
  const toastStore = getToastStore()
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey

  const UNLEARNING_LLM_PRODUCTS: LlmTieredProduct[] = [
    Product.UNLEARNING_LLM_BEHAVIOR,
    Product.UNLEARNING_LLM_DATA,
  ]
  const RUNNABLE_LLM_PRODUCTS: LlmTieredProduct[] = [
    ...UNLEARNING_LLM_PRODUCTS,
    Product.LLM_BEHAVIOR_EVAL,
  ]
  const DEFAULT_LOCAL_LLM_MODEL_ROOT = '/opt/hirundo/llm-models'
  const PARAMETERS_PER_BILLION = 1_000_000_000

  interface LlmModalRestoreState {
    modelInputValues: ModelInput
  }
  const isLlmModalRestoreState = (v: unknown): v is LlmModalRestoreState =>
    !!v && typeof v === 'object' && 'modelInputValues' in (v as Record<string, unknown>)

  let config = $state<Config | null>(null)
  const availableLlmSourceTypes = $derived(
    config?.airGapped ? [LlmSourceType.LOCAL_TRANSFORMERS] : llmSourceTypesValues,
  )
  const restrictedModelConfig = $derived(getRestrictedModelConfig(config))
  const restrictedModelIds = $derived(restrictedModelConfig.restrictedModelIds)
  const isRestrictedMode = $derived(restrictedModelConfig.isRestrictedMode)
  const localLlmModelRoot = $derived(
    (config?.localLlmModelRoot ?? DEFAULT_LOCAL_LLM_MODEL_ROOT).replace(/\/+$/, ''),
  )
  const localLlmModelPrefix = $derived(`file://${localLlmModelRoot}/`)

  const currentUser: User = $loggedInStore

  let model: ILlm | null = $modalStore[0].value
  const action = $derived(model && !$modalStore[0].meta?.duplicate ? 'update' : 'create')
  let currentOrganizationId = model?.organizationId ?? currentUser.primaryOrganizationId
  let modelFormElement: HTMLFormElement | undefined = $state()

  const readOnly = $modalStore[0]?.meta?.readOnly || false

  type ModelInput = Partial<Pick<ILlm, 'organizationId'>> & Pick<ILlm, 'modelName' | 'modelSource'>

  let modelInputValues: ModelInput = $state({
    organizationId: currentOrganizationId,
    modelName: '',
    modelSource: new HuggingFaceTransformersSource(),
  })
  let invalids = $state({
    modelName: new ValidityState(),
    modelSource: new ValidityState(),
    huggingfaceModelName: new ValidityState(),
    huggingfaceToken: new ValidityState(),
    huggingfaceRevision: new ValidityState(),
    huggingfaceCodeRevision: new ValidityState(),
    transformersLocalPath: new ValidityState(),
    localParameterCount: new ValidityState(),
  })

  let isEditMode = $state(false)
  let isLoading = $state(false)
  let isCheckingHuggingFaceAccess = $state(false)
  let huggingfaceAccessMessage = $state('')
  let huggingfaceParameterLabel = $state('')
  let huggingfaceAccessStatus = $state('idle')
  let saveModelSourceToken = $state(false)
  let modelSourceTokenName = $state('')
  let modelSourceToken = $state<string | null | undefined>(null)
  let modelSourceTokenId = $state<number | undefined>(undefined)
  const saveTokenMutation = createSaveHuggingFaceTokenMutation(queryClient)

  const syncModelSourceTokenStateFromSource = () => {
    if (!isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
      modelSourceToken = null
      modelSourceTokenId = undefined
      saveModelSourceToken = false
      modelSourceTokenName = ''
      return
    }
    modelSourceToken = modelInputValues.modelSource.token
    modelSourceTokenId = modelInputValues.modelSource.tokenId
  }

  const getModelSourceValidationMessage = (
    modelSource: ModelInput['modelSource'],
    resolvedConfig: Config | null,
  ): string | null => {
    if (!llmSourceTypesValues.includes(modelSource.type)) {
      return 'Model source type is not supported.'
    }
    if (!resolvedConfig?.airGapped && modelSource.type === LlmSourceType.LOCAL_TRANSFORMERS) {
      return 'Local model paths are only available in air-gapped mode.'
    }
    if (resolvedConfig?.airGapped && modelSource.type === LlmSourceType.HUGGINGFACE_TRANSFORMERS) {
      return 'HuggingFace model sources cannot be used in air-gapped mode. Use a local model path under /opt/hirundo/llm-models.'
    }
    return null
  }

  const validateModelSource = (): boolean => {
    if (!config) {
      invalids.modelSource.setValid()
      return true
    }
    const validationMessage = getModelSourceValidationMessage(modelInputValues.modelSource, config)
    if (validationMessage) {
      invalids.modelSource.setInvalid(validationMessage)
      return false
    }
    invalids.modelSource.setValid()
    return true
  }

  const resetHuggingFaceAccessState = () => {
    huggingfaceAccessStatus = 'idle'
    huggingfaceAccessMessage = ''
    huggingfaceParameterLabel = ''
    invalids.huggingfaceModelName.setValid()
  }

  const formatParameterCount = (parameterCount: number | null): string => {
    if (!parameterCount) {
      return 'Model size: unavailable.'
    }
    return `Model size: ${formatParameterCountShort(parameterCount)} parameters.`
  }

  const toBillions = (parameterCount: number): number => parameterCount / PARAMETERS_PER_BILLION

  const fromBillions = (parameterCountInBillions: number): number =>
    Math.round(parameterCountInBillions * PARAMETERS_PER_BILLION)

  const getLocalModelFieldValue = (localPath: string): string => {
    if (!localPath) {
      return ''
    }
    if (localPath.startsWith('file://')) {
      return localPath
    }
    if (localPath === localLlmModelRoot) {
      return localLlmModelPrefix
    }
    if (localPath.startsWith(`${localLlmModelRoot}/`)) {
      return `${localLlmModelPrefix}${localPath.slice(localLlmModelRoot.length + 1)}`
    }
    return localPath
  }

  const normalizeAbsolutePath = (localPath: string): string | null => {
    if (!localPath.startsWith('/')) {
      return null
    }
    const pathParts: string[] = []
    for (const pathPart of localPath.split('/')) {
      if (!pathPart || pathPart === '.') {
        continue
      }
      if (pathPart === '..') {
        if (!pathParts.pop()) {
          return null
        }
        continue
      }
      pathParts.push(pathPart)
    }
    return `/${pathParts.join('/')}`
  }

  const decodeLocalPath = (localPath: string): string | null => {
    try {
      return decodeURIComponent(localPath)
    } catch {
      return null
    }
  }

  const getLocalModelPathValidationMessage = (localPath: string): string | null => {
    if (!localPath) {
      return 'Local path is required'
    }

    let candidatePath: string
    if (localPath.startsWith('file://')) {
      const decodedPath = decodeLocalPath(localPath.slice('file://'.length))
      if (!decodedPath) {
        return 'Local path is invalid'
      }
      candidatePath = decodedPath
    } else if (localPath.startsWith('/')) {
      candidatePath = localPath
    } else {
      candidatePath = `${localLlmModelRoot}/${localPath}`
    }

    const normalizedRoot = normalizeAbsolutePath(localLlmModelRoot)
    const normalizedPath = normalizeAbsolutePath(candidatePath)
    if (!normalizedRoot || !normalizedPath || normalizedPath === normalizedRoot) {
      return 'Local path is required'
    }
    if (!normalizedPath.startsWith(`${normalizedRoot}/`)) {
      return `Local path must be under ${localLlmModelRoot}`
    }
    return null
  }

  const getModelValidationProducts = (): LlmTieredProduct[] => {
    const activeLlmProducts = RUNNABLE_LLM_PRODUCTS.filter((product) =>
      config?.activeProducts?.includes(product),
    )
    if (activeLlmProducts.length > 0) {
      return activeLlmProducts
    }
    return UNLEARNING_LLM_PRODUCTS
  }

  const validateHuggingFaceAccess = async (): Promise<boolean> => {
    if (!isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
      resetHuggingFaceAccessState()
      return true
    }
    const modelName = modelInputValues.modelSource.modelName.trim()
    if (!modelName) {
      invalids.huggingfaceModelName.setInvalid('HuggingFace model ID is required.')
      return false
    }

    isCheckingHuggingFaceAccess = true
    huggingfaceAccessStatus = 'checking'
    huggingfaceAccessMessage = 'Checking HuggingFace model access...'
    if (modelInputValues.modelSource.tokenId) {
      isCheckingHuggingFaceAccess = false
      invalids.huggingfaceModelName.setValid()
      huggingfaceAccessStatus = 'success'
      huggingfaceAccessMessage = ''
      huggingfaceParameterLabel = 'Model access will be validated using the saved token.'
      return true
    }

    const accessResult = await checkHuggingFaceModelAccess({
      modelId: modelName,
      token: modelInputValues.modelSource.token,
    })

    isCheckingHuggingFaceAccess = false

    if (accessResult.status === 'accessible') {
      invalids.huggingfaceModelName.setValid()
      huggingfaceAccessStatus = 'success'
      huggingfaceAccessMessage = ''
      huggingfaceParameterLabel = formatParameterCount(accessResult.parameterCount)
      const tierValidationMessage = getLlmTierValidationMessage({
        parameterCount: accessResult.parameterCount,
        activeProductEntitlements:
          config?.activeProductEntitlements ?? config?.activeProducts ?? [],
        candidateProducts: getModelValidationProducts(),
      })
      if (tierValidationMessage) {
        invalids.huggingfaceModelName.setInvalid(tierValidationMessage)
        huggingfaceAccessStatus = 'error'
        huggingfaceAccessMessage = tierValidationMessage
        return false
      }
      return true
    }

    const failureMessage =
      accessResult.status === 'unauthorized'
        ? 'This HuggingFace model is gated or private. Provide a token with access.'
        : 'Unable to access HuggingFace model metadata. Check the model ID and token.'
    invalids.huggingfaceModelName.setInvalid(failureMessage)
    huggingfaceAccessStatus = 'error'
    huggingfaceAccessMessage = failureMessage
    huggingfaceParameterLabel = ''
    return false
  }

  onMount(async () => {
    await initializeInputValues(model)
    syncModelSourceTokenStateFromSource()
    config = await getConfig(toastStore)
    if (!model && config.airGapped) {
      modelInputValues.modelSource = new LocalTransformersSource()
      syncModelSourceTokenStateFromSource()
    }
    validateModelSource()

    // Restore previously cached state if present
    if (
      $modalStore[0]?.meta?.restoreState &&
      isLlmModalRestoreState($modalStore[0].meta.restoreState)
    ) {
      const restoreState = $modalStore[0].meta.restoreState
      modelInputValues = restoreState.modelInputValues
      syncModelSourceTokenStateFromSource()
      validateModelSource()
      resetHuggingFaceAccessState()
    }
  })

  $effect(() => {
    if (!isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
      return
    }
    modelInputValues.modelSource.token = modelSourceToken ?? null
    modelInputValues.modelSource.tokenId = modelSourceTokenId
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    setRestoreState(restoreKey, {
      modelInputValues,
    } satisfies LlmModalRestoreState)
  })

  $effect(() => {
    if (!config) {
      return
    }
    validateModelSource()
  })

  const initializeInputValues = async (model: ILlm | null) => {
    if (!model) {
      return
    }

    isEditMode = !$modalStore[0].meta?.duplicate // Disables the accordion and search input unless duplicating

    const { organizationId, modelName, modelSource } = model

    const newName = $modalStore[0].meta.newName

    modelInputValues = {
      organizationId,
      modelName: newName ? newName : modelName,
      modelSource,
    }
    syncModelSourceTokenStateFromSource()
  }

  const createModel = async (): Promise<ICreateLlm> => {
    try {
      const { organizationId, modelName, modelSource } = modelInputValues
      const payload = {
        organizationId,
        modelName,
        modelSource,
      }

      if (!$modalStore[0].response) {
        console.error("Modal doesn't have a response function")
        throw new Error("Modal doesn't have a response function")
      }
      await $modalStore[0].response({
        payload: {
          organizationId,
          modelName,
          modelSource: $state.snapshot(modelSource), // Serialize class properly
        },
      })
      return payload
    } catch (e) {
      console.error(`Failed to ${action} LLM`)
      throw e
    }
  }

  const persistModelSourceTokenIfRequested = async (): Promise<boolean> => {
    if (!isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
      return true
    }
    if (!saveModelSourceToken) {
      return true
    }
    try {
      await persistSavedHuggingFaceToken({
        shouldSave: saveModelSourceToken,
        token: modelSourceToken,
        tokenName: modelSourceTokenName,
        mutation: $saveTokenMutation,
        onSuccess: (createdTokenId) => {
          modelSourceTokenId = createdTokenId
          modelSourceToken = null
          saveModelSourceToken = false
          modelSourceTokenName = ''
        },
      })
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save HuggingFace token.'
      toastStore.trigger({
        message,
        background: 'variant-filled-error',
        autohide: false,
      })
      return false
    }
  }

  const canRunCreatedModel = async () => {
    const effectiveConfig = config ?? (await getConfig(toastStore))
    config = effectiveConfig
    const effectiveRestrictedModelIds = effectiveConfig.restrictedModelIds ?? []

    if (effectiveRestrictedModelIds.length === 0) {
      return true
    }
    if (effectiveConfig.activeProducts?.includes(Product.LLM_BEHAVIOR_EVAL)) {
      return true
    }
    return isModelSourceApprovedForUnlearning(
      modelInputValues.modelSource,
      effectiveRestrictedModelIds,
    )
  }

  const createHandler = async () => {
    if (isLoading) {
      return
    }
    if (!validateModelSource()) {
      return
    }
    if (!modelFormElement?.reportValidity()) {
      return
    }
    isLoading = true
    try {
      if (!(await validateHuggingFaceAccess())) {
        return
      }
      if (!(await persistModelSourceTokenIfRequested())) {
        return
      }
      await createModel()
      parent.onClose()
    } finally {
      isLoading = false
    }
  }

  const createAndRunHandler = async () => {
    if (isLoading) {
      return
    }
    if (!validateModelSource()) {
      return
    }
    if (!modelFormElement?.reportValidity()) {
      return
    }
    isLoading = true
    try {
      if (!(await validateHuggingFaceAccess())) {
        return
      }
      if (!(await canRunCreatedModel())) {
        toastStore.trigger({
          message: 'Create and run is only available for approved models in this environment.',
          background: 'variant-filled-error',
          autohide: false,
        })
        return
      }
      if (!(await persistModelSourceTokenIfRequested())) {
        return
      }
      const model = await createModel()
      const modelId = $modalStore[0].meta.modelId
      if (!modelId) {
        console.error(`Failed to ${action} LLM. Could not run unlearning`)
        return
      }
      await openRunOptionsModal({
        modalStore,
        toastStore,
        model,
        runOptions: {
          llmId: modelId,
          llmName: modelInputValues.modelName,
          modelSource: $state.snapshot(model.modelSource),
          config,
        },
      })
      parent.onClose()
    } finally {
      isLoading = false
    }
  }
</script>

{#snippet invalidIcon(field: keyof typeof invalids, value?: string | null)}
  <InvalidIcon {invalids} {field} {value}></InvalidIcon>
{/snippet}
{#snippet invalidHoverInfo(field: keyof typeof invalids)}
  <InvalidHoverInfo {invalids} {field}></InvalidHoverInfo>
{/snippet}

{#if $modalStore[0]}
  <form
    class="card relative m-3 h-[32rem] max-h-[90vh] w-[48rem] max-w-[80vw] justify-self-center overflow-y-scroll px-6"
    bind:this={modelFormElement}
    class:dimmed={isLoading}
  >
    {#if isLoading}
      <div
        class="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-secondary-400 bg-opacity-20"
      >
        <ProgressRadial
          stroke={75}
          meter="stroke-surface-200"
          track="stroke-surface-200/30"
          strokeLinecap="round"
          indeterminate={true}
        />
      </div>
    {/if}
    <h3 class="h3 justify-between py-4">
      {$modalStore[0].title}
      <CloseModalButton onClose={parent.onClose} />
    </h3>
    <div class="m-1">{$modalStore[0].body}</div>
    <RequiredNameField
      id="modelName"
      name="modelName"
      label="Model name:"
      {invalids}
      field="modelName"
      bind:value={modelInputValues.modelName}
      {readOnly}
      placeholder="e.g. distilgpt2-test"
    />

    <label for="organization-to-associate-with">Associated organization:</label>
    {#if readOnly}
      <ReadOnly
        value={modelInputValues.organizationId
          ? $organizationsStore.get(modelInputValues.organizationId)?.name
          : ''}
      />
    {:else if $organizationAsyncState.loading}
      <p>Loading organizations...</p>
    {:else if $organizationAsyncState.error}
      <p>{$organizationAsyncState.error}</p>
    {:else}
      <Select
        required
        id="organization-to-associate-with"
        name="organization-to-associate-with"
        bind:value={modelInputValues.organizationId}
      >
        <option selected value>Select organization</option>
        {#each $organizationsStore as [_id, organization] (organization.id)}
          <option
            value={organization.id}
            selected={organization.id === modelInputValues.organizationId}
          >
            {organization.name}
          </option>
        {/each}
      </Select>
    {/if}

    {#if config && config.airGapped}
      <label for="metadataType">AI provider:</label>
      {#if readOnly}
        <div class="input-group input-group-divider grid-cols-[1fr_auto]">
          <ReadOnly
            value={displayLlmSourceTypes[modelInputValues.modelSource.type] ??
              modelInputValues.modelSource.type}
          />
          {@render invalidIcon('modelSource')}
        </div>
        {@render invalidHoverInfo('modelSource')}
      {:else}
        <div class="input-group input-group-divider grid-cols-[1fr_auto]">
          <Select
            name="metadataType"
            placeholder="Select Type"
            required
            value={modelInputValues.modelSource.type}
            onchange={(event) => {
              switch (event.currentTarget.value) {
                case LlmSourceType.HUGGINGFACE_TRANSFORMERS:
                  modelInputValues.modelSource = new HuggingFaceTransformersSource()
                  syncModelSourceTokenStateFromSource()
                  break
                case LlmSourceType.LOCAL_TRANSFORMERS:
                  modelInputValues.modelSource = new LocalTransformersSource()
                  syncModelSourceTokenStateFromSource()
                  break
              }
              resetHuggingFaceAccessState()
              validateModelSource()
            }}
          >
            <option selected value>Select type</option>
            {#each availableLlmSourceTypes as llmSourceType (llmSourceType)}
              <option value={llmSourceType}>{displayLlmSourceTypes[llmSourceType]}</option>
            {/each}
          </Select>
          {@render invalidIcon('modelSource')}
        </div>
        {@render invalidHoverInfo('modelSource')}
      {/if}
    {/if}

    {#if isHuggingFaceTransformersSource(modelInputValues.modelSource)}
      <label for="huggingfaceModelName">HuggingFace model ID:</label>
      {#if isRestrictedMode}
        <div class="alert variant-filled-surface">
          <div class="alert-message space-y-1">
            <p class="text-sm">
              Evaluation supports any accessible HuggingFace model. Behavior unlearning is limited
              to approved models.
            </p>
            <p class="text-surface-600-300 text-sm">
              Approved for unlearning:
              {#each restrictedModelIds as restrictedModelId, index (restrictedModelId)}
                <span
                  class="variant-filled-primary badge mt-1 inline-flex selection:bg-slate-200 selection:text-slate-900"
                >
                  {restrictedModelId}
                </span>{#if index < restrictedModelIds.length - 1},
                {/if}
              {/each}
            </p>
          </div>
        </div>
      {/if}
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.modelName} />
      {:else}
        <div class="input-group input-group-divider grid-cols-[1fr_auto]">
          <input
            class="autocomplete my-0 box-border flex px-2 py-1 md:min-w-96 {invalids
              .huggingfaceModelName.invalid
              ? ' invalid'
              : ''}"
            type="text"
            id="huggingfaceModelName"
            value={modelInputValues.modelSource.modelName}
            oninvalid={huggingfaceRepoNameValidator(invalids.huggingfaceModelName)}
            onchange={(event) => {
              const validator = huggingfaceRepoNameValidator(invalids.huggingfaceModelName)
              validator(event)
              if (isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
                modelInputValues.modelSource.modelName = event.currentTarget.value
                resetHuggingFaceAccessState()
              }
            }}
            required
            pattern={HUGGINGFACE_REPO_NAME_PATTERN.source}
            placeholder="e.g. distilbert/distilgpt2-test"
          />
          {@render invalidIcon('huggingfaceModelName', modelInputValues.modelSource.modelName)}
        </div>
        {@render invalidHoverInfo('huggingfaceModelName')}
        <p class="text-sm text-gray-500">Format: owner/model-name</p>
        {#if isCheckingHuggingFaceAccess}
          <p class="text-sm text-gray-500">Checking HuggingFace model access...</p>
        {:else if huggingfaceAccessStatus === 'success'}
          <p class="text-sm text-gray-500">{huggingfaceParameterLabel}</p>
        {:else if huggingfaceAccessStatus === 'error'}
          <p class="text-sm text-red-500">{huggingfaceAccessMessage}</p>
        {/if}
        <HuggingFaceTokenPicker
          bind:token={modelSourceToken}
          bind:tokenId={modelSourceTokenId}
          bind:saveToken={saveModelSourceToken}
          bind:tokenName={modelSourceTokenName}
          customLabel="HuggingFace token (optional)"
        />
        <p class="text-sm text-gray-500">Only required for private or gated models.</p>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label for="huggingfaceRevision" class="mb-2">Version:</label>
            <div class="input-group input-group-divider grid-cols-[1fr_auto]">
              <input
                class="autocomplete my-0 box-border flex px-2 py-1 md:min-w-96 {invalids
                  .huggingfaceRevision.invalid
                  ? ' invalid'
                  : ''}"
                type="text"
                id="huggingfaceRevision"
                value={modelInputValues.modelSource.revision}
                onchange={(event) => {
                  if (isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
                    modelInputValues.modelSource.revision = event.currentTarget.value || null
                    if (modelInputValues.modelSource.revision) {
                      invalids.huggingfaceRevision.setValid()
                    } else {
                      invalids.huggingfaceRevision.setInvalid('HuggingFace revision is required')
                    }
                  }
                }}
                placeholder="e.g. main, v1.0, commit hash"
                defaultValue="main"
              />
              {@render invalidIcon('huggingfaceRevision', modelInputValues.modelSource.revision)}
            </div>
            {@render invalidHoverInfo('huggingfaceRevision')}
          </div>
          <div>
            <label for="huggingfaceCodeRevision" class="mb-2">HuggingFace code revision:</label>
            <div class="input-group input-group-divider grid-cols-[1fr_auto]">
              <input
                class="autocomplete my-0 box-border flex px-2 py-1 md:min-w-96 {invalids
                  .huggingfaceCodeRevision.invalid
                  ? ' invalid'
                  : ''}"
                type="text"
                id="huggingfaceCodeRevision"
                value={modelInputValues.modelSource.codeRevision}
                onchange={(event) => {
                  if (isHuggingFaceTransformersSource(modelInputValues.modelSource)) {
                    modelInputValues.modelSource.codeRevision = event.currentTarget.value || null
                    if (modelInputValues.modelSource.codeRevision) {
                      invalids.huggingfaceCodeRevision.setValid()
                    } else {
                      invalids.huggingfaceCodeRevision.setInvalid(
                        'HuggingFace code revision is required',
                      )
                    }
                  }
                }}
                placeholder="e.g. main, v1.0, commit hash"
                defaultValue="main"
              />
              {@render invalidIcon(
                'huggingfaceCodeRevision',
                modelInputValues.modelSource.codeRevision,
              )}
            </div>
            {@render invalidHoverInfo('huggingfaceCodeRevision')}
          </div>
        </div>
      {/if}
    {:else if isLocalTransformersSource(modelInputValues.modelSource)}
      <label for="transformersLocalPath">Local path:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.localPath} />
      {:else}
        <MetadataFields
          prefix={localLlmModelPrefix}
          {invalids}
          fieldName="transformersLocalPath"
          fieldValue={getLocalModelFieldValue(modelInputValues.modelSource.localPath)}
          setField={(newValue) => {
            if (!isLocalTransformersSource(modelInputValues.modelSource)) {
              console.error('Model source is not LocalTransformers')
              return
            }
            modelInputValues.modelSource.localPath = newValue
            const validationMessage = getLocalModelPathValidationMessage(newValue)
            if (validationMessage) {
              invalids.transformersLocalPath.setInvalid(validationMessage)
            } else {
              invalids.transformersLocalPath.setValid()
            }
          }}
        />
        <label for="localParameterCount">Parameter count (B):</label>
        <div class="input-group input-group-divider grid-cols-[1fr_auto]">
          <input
            class="my-0 box-border flex px-2 py-1 md:min-w-96 {invalids.localParameterCount.invalid
              ? ' invalid'
              : ''}"
            id="localParameterCount"
            type="number"
            min="0.01"
            step="0.01"
            value={modelInputValues.modelSource.parameterCount
              ? String(toBillions(modelInputValues.modelSource.parameterCount))
              : ''}
            placeholder="e.g. 7 or 0.35"
            onchange={(event) => {
              if (!isLocalTransformersSource(modelInputValues.modelSource)) {
                console.error('Model source is not LocalTransformers')
                return
              }
              const nextValue = event.currentTarget.value
              if (!nextValue) {
                modelInputValues.modelSource.parameterCount = null
                invalids.localParameterCount.setValid()
                return
              }
              const parameterCountInBillions = Number(nextValue)
              const parameterCount = fromBillions(parameterCountInBillions)
              if (
                Number.isFinite(parameterCountInBillions) &&
                Number.isSafeInteger(parameterCount) &&
                parameterCount > 0
              ) {
                modelInputValues.modelSource.parameterCount = parameterCount
                invalids.localParameterCount.setValid()
              } else {
                invalids.localParameterCount.setInvalid(
                  'Parameter count must be a positive number in billions',
                )
              }
            }}
          />
          {@render invalidIcon(
            'localParameterCount',
            modelInputValues.modelSource.parameterCount?.toString(),
          )}
        </div>
        {@render invalidHoverInfo('localParameterCount')}
        <p class="text-sm text-gray-500">
          Used for worker queue selection when the local path does not include a size such as 7B.
        </p>
      {/if}
    {/if}

    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <div class="flex w-full items-center justify-between">
        {#if !readOnly}
          <div class="variant-filled-primary btn-group space-x-2">
            <button
              class="variant-filled-primary {parent.buttonPositive}"
              type="button"
              onclick={createHandler}
              disabled={isLoading}
            >
              {parent.buttonTextSubmit}
            </button>
            <button
              class="variant-filled-error"
              type="button"
              onclick={parent.onClose}
              disabled={isLoading}
            >
              {parent.buttonTextCancel}
            </button>
          </div>
        {/if}
        {#if !isEditMode}
          <div class="variant-filled-primary btn-group">
            <button
              class="variant-filled-primary"
              type="button"
              onclick={createAndRunHandler}
              disabled={isLoading}
              aria-label="Create and run"
            >
              <Icon icon="iconoir:play-solid" />
            </button>
          </div>
        {/if}
      </div>
    </footer>
  </form>
{/if}

<style lang="postcss">
  form {
    flex-direction: column;
  }
  form > :not(:first-child) {
    margin: 0.5rem 0;
  }

  form input.invalid {
    @apply input-error text-black;
  }
  .disable-accordion {
    @apply pointer-events-none cursor-not-allowed opacity-50;
  }
</style>
